///
/// EarthBrowser
///
/// Created by Matt Giger on 10/18/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Render-to-texture pass that packs a texture series' current frame pair
/// (two single-channel textures) into one RG8 texture (frame_pair.glsl).
/// The earth tile fragment shader has 16 texture units and every weather
/// overlay used two of them for its frame pair; sampling the pair from one
/// texture halves that, which is what lets clouds, radar, precipitation
/// and sea temperature coexist with the tile's own textures.
///
/// Runs from TextureSeries.updateState, i.e. during the render traversal
/// before the frame's draw calls execute, so it restores the GL state it
/// touches. A repack happens only when the pair changes (a frame advance
/// or a frame finishing its load), not per rendered frame.
///

import { FrameBuffer } from "@stovokor/shader/FrameBuffer"
import { ComponentType, DrawMode, InternalFormat, PixelFormat, TextureFilter, TextureWrap } from "@stovokor/shader/GLConstants"
import { type ShaderProgram } from "@stovokor/shader/ShaderProgram"
import { ShaderSourceDefinition } from "@stovokor/shader/ShaderSourceCode"
import { Texture } from "@stovokor/shader/Texture"
import { VertexAttribute } from "@stovokor/shader/VertexAttribute"
import { VertexBuffer } from "@stovokor/shader/VertexBuffer"
import { type StovokorContext } from "@stovokor/StovokorContext"

export class FramePairPacker {
	private context: StovokorContext
	private shader: ShaderProgram
	private framebuffer: FrameBuffer
	private position: VertexAttribute
	private vertexBuffer: VertexBuffer

	constructor(context: StovokorContext) {
		this.context = context
		this.shader = context.getCachedShader(new ShaderSourceDefinition("frame_pair"))
		this.framebuffer = new FrameBuffer(context)
		this.position = new VertexAttribute(context, 2, [-1, -1, 3, -1, -1, 3])
		this.vertexBuffer = new VertexBuffer(context, [this.position])
	}

	public destroy(): void {
		this.framebuffer.destroy()
		this.vertexBuffer.destroy()
	}

	/**
	 * A 1x1 RG8 target, grown by pack() to the frames' size. LINEAR
	 * filtered and wrapping horizontally like the global overlay frames
	 */
	public createTarget(): Texture {
		return new Texture(this.context, 1, 1,
			InternalFormat.RG8, PixelFormat.RG, ComponentType.UnsignedByte,
			TextureFilter.Linear, TextureFilter.Linear,
			TextureWrap.Repeat, TextureWrap.ClampToEdge)
	}

	/**
	 * Pack frame0 into target.r and frame1 into target.g at frame0's
	 * resolution (frame1 normally matches; a blank 1x1 frame just reads as
	 * zero). With mipmaps set the chain is rebuilt afterward. Returns false,
	 * leaving the target untouched, if the pass can't run yet.
	 */
	public pack(frame0: Texture, frame1: Texture, target: Texture, mipmaps: boolean): boolean {
		if(!this.shader.available || !frame0.textureId || !frame1.textureId || !target.textureId) {
			return false
		}

		const width = Math.max(frame0.width, 1)
		const height = Math.max(frame0.height, 1)
		if(target.width !== width || target.height !== height) {
			target.setFilters(TextureFilter.Linear, TextureFilter.Linear)
			target.initialize(width, height)
		}

		// Save/restore through the engine's state cache: no getParameter
		// round trip, and the cache stays exact for the next frame
		const glState = this.context.glState
		const viewport = glState.getViewport()

		try {
			this.framebuffer.attach(WebGL2RenderingContext.COLOR_ATTACHMENT0, target)
			this.framebuffer.bind()
		} catch(error) {
			this.framebuffer.unbind()
			this.context.logger.warn("FramePairPacker: framebuffer incomplete", error)
			return false
		}
		glState.viewport(0, 0, width, height)
		glState.disable(WebGL2RenderingContext.DEPTH_TEST)
		glState.disable(WebGL2RenderingContext.BLEND)

		this.shader.bindAction()()
		this.shader.setupAttributeActionsFrom({ position: this.position }).forEach(action => action())
		this.shader.uniforms.frame0?.bindingFunction(frame0)()
		this.shader.uniforms.frame1?.bindingFunction(frame1)()
		this.vertexBuffer.draw(DrawMode.Triangles)
		this.shader.disableAttributeActions().forEach(action => action())
		this.shader.unbindAction()()

		this.framebuffer.unbind()
		glState.enable(WebGL2RenderingContext.DEPTH_TEST)
		glState.enable(WebGL2RenderingContext.BLEND)
		glState.viewport(viewport[0], viewport[1], viewport[2], viewport[3])

		if(mipmaps) {
			target.generateMipmaps()
		}
		target.available = true
		return true
	}
}
